import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsDemoI18n } from './i18n';
import { getNoClientsDemoPaged } from '../../services/noClientsDemoService';
import { persistStateToUrl } from 'utils/urlState';
import './noClients.scss';
import usePermissionsAI from 'permissions/modules/artificialIntelligence/usePermissionsAI';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' })
    : '--';

const LostDealsList = ({ DesignSystemComponents }) => {
  const { Card, Table, IconButton, Input } = DesignSystemComponents || {};

  const navigate = useNavigate();
  const { t } = useScopedTranslation(noClientsDemoI18n);
  const { canViewNoClientesCall } = usePermissionsAI();

  // Estado de la tabla persistido en la URL (query params): al entrar al detalle y volver,
  // la búsqueda, el orden, la página y las filas por página quedan como estaban. Mismo patrón
  // que LegalCases/DailyState (utils/urlState.persistStateToUrl).
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => Number(searchParams.get('page') ?? 0));
  const [pageSize, setPageSize] = useState(() =>
    Number(searchParams.get('pageSize') ?? 10)
  );
  const [sortKey, setSortKey] = useState(
    () => searchParams.get('sortKey') ?? null
  );
  const [sortDirection, setSortDirection] = useState(
    () => searchParams.get('sortDir') ?? null
  );
  const [searchDraft, setSearchDraft] = useState(
    () => searchParams.get('search') ?? ''
  );
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // Sincroniza el estado de la tabla con la URL. Un único punto de escritura, reactivo a los
  // valores YA actualizados → evita el "valor atrasado" (antes se escribía en cada handler con
  // closures desfasadas y el reset de página del componente pisaba el pageSize nuevo).
  useEffect(() => {
    persistStateToUrl({
      filters: { search },
      sortKey,
      sortDirection,
      page,
      pageSize,
      setSearchParams,
    });
  }, [page, pageSize, sortKey, sortDirection, search, setSearchParams]);

  const handleViewClient = useCallback(
    (client) => {
      if (!client?.dealId) return;
      navigate(`/ia/no-clientes/cliente/${encodeURIComponent(client.dealId)}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'dealId',
        label: t('nc_col_dealId'),
        sort: true,
        width: '100px',
      },
      {
        accessor: 'dealName',
        label: t('nc_col_name'),
        sort: true,
        width: '300px',
      },
      {
        accessor: 'phone',
        label: t('nc_col_phone'),
        sort: true,
        width: '110px',
        render: (row) => row.phone || '--',
      },
      {
        accessor: 'hubspotOwnerName',
        label: t('nc_col_owner'),
        sort: true,
        render: (row) => row.hubspotOwnerName || '--',
      },
      {
        accessor: 'callCount',
        label: t('nc_col_callCount'),
        sort: true,
        align: 'right',
        width: '90px',
        render: (row) => row.callCount ?? '--',
      },
      {
        accessor: 'createDate',
        label: t('nc_col_createDate'),
        sort: true,
        render: (row) => formatDate(row.createDate),
      },
      {
        accessor: 'lastCallDate',
        label: t('nc_col_lastCall'),
        sort: true,
        render: (row) => formatDate(row.lastCallDate),
      },
      {
        accessor: 'closeDate',
        label: t('nc_col_closeDate'),
        sort: true,
        render: (row) => formatDate(row.closeDate),
      },
      {
        accessor: 'lostReason',
        label: t('nc_col_lostReason'),
        sort: true,
        width: '220px',
        render: (row) => row.lostReason || '--',
      },
      {
        label: t('nc_col_actions'),
        align: 'right',
        width: '90px',
        render: (row) =>
          row?.dealId ? (
            <div className='d-flex'>
              <IconButton
                ariaLabel={t('nc_aria_viewAnalysis')}
                icon='eye'
                size='md'
                iconStroke='var(--primary-500)'
                onClick={() => handleViewClient(row)}
                disabled={!canViewNoClientesCall}
              />
            </div>
          ) : (
            <span className='color-gray-600'>--</span>
          ),
      },
    ],
    [IconButton, handleViewClient, canViewNoClientesCall, t]
  );

  const handleServerSortChange = ({ sortKey: key, direction } = {}) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextSearch = searchDraft.trim();
      setSearch((prev) => {
        if (prev === nextSearch) return prev;
        setPage(0);
        return nextSearch;
      });
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [searchDraft]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchLostDeals = async () => {
      try {
        setLoading(true);
        const response = await getNoClientsDemoPaged({
          page: page + 1,
          pageSize,
          sortBy: sortKey,
          desc: sortDirection ? sortDirection === 'desc' : null,
          search: search || null,
          signal: controller.signal,
        });
        setRows(Array.isArray(response?.data) ? response.data : []);
        setTotalItems(Number(response?.total) || 0);
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error loading lost deals:', error);
          setRows([]);
          setTotalItems(0);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLostDeals();
    return () => controller.abort();
  }, [page, pageSize, sortKey, sortDirection, search]);

  return (
    <div className='no-clients-page'>
      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('nc_pageTitle')}</h2>
        <h4 className='fw-normal'>{t('nc_pageSubtitle')}</h4>
      </div>

      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='mb-3'>
          <Input
            icon='search'
            name='searchNoClients'
            placeholder={t('nc_searchPlaceholder')}
            type='text'
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </div>
        <div className='table-container'>
          <Table
            columns={columns}
            data={rows}
            loading={loading}
            paginator
            paginationMode='server'
            page={page}
            onPageChange={handlePageChange}
            initialPageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            totalItems={totalItems}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default LostDealsList;
